import React from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Swal from "sweetalert2";
import axios from "axios";
import { getAuthCredentials } from "../../../utils/auth";
import { validateOTP } from "../../../component/OTPValidator";

const ReceiptActionModal = ({ open, handleClose, handleAction, receipt }) => {
  const { posId, receiptNumber, paymentType, tenderedAmount, status, total } =
    receipt;

  const handleOverride = async () => {
    handleClose();

    const { status: otpStatus } = await validateOTP(
      `Override Receipt #${receiptNumber}`
    );
    if (otpStatus !== "success") return;

    let amountToSend = total;
    let selectedSalesType = paymentType;
    let subTenderDetails = "";
    let subTenderType = "";

    if (paymentType === "Cash" && status === "pending") {
      const { isConfirmed: changeTypeConfirmed } = await Swal.fire({
        title: "Change Sales Type?",
        text: "Do you want to change this to E-Cash?",
        icon: "question",
        showCancelButton: true,
        confirmButtonText: "Change to E-Cash",
        cancelButtonText: "Keep as Cash",
      });

      if (changeTypeConfirmed) {
        selectedSalesType = "E-Cash";
      }
    }

    if (selectedSalesType === "Cash") {
      const result = await Swal.fire({
        title: "Enter Tender Amount",
        input: "number",
        inputLabel: "Customer's Tender Amount",
        inputAttributes: {
          min: 0,
          step: 0.01,
        },
        inputValue: total.toString(),
        showCancelButton: true,
        confirmButtonText: "Proceed",
        cancelButtonText: "Cancel",
        inputValidator: (value) => {
          if (!value || isNaN(value) || Number(value) <= 0) {
            return "Please enter a valid amount.";
          }
          return null;
        },
      });

      if (!result.isConfirmed) return;
      amountToSend = Number(result.value);
    } else {
      // Step 1: Choose subTenderType
      const { value: selectedSubTender } = await Swal.fire({
        title: "Select E-Cash Type",
        text: "Choose the sub-tender type",
        input: "radio",
        inputOptions: {
          QRPH: "QRPH",
          Card: "Card",
        },
        inputValidator: (value) => {
          if (!value) return "You must choose one.";
          return null;
        },
        confirmButtonText: "Next",
        showCancelButton: true,
      });

      if (!selectedSubTender) return;
      subTenderType = selectedSubTender;

      // Step 2: Enter Reference Number
      const result = await Swal.fire({
        title: "Enter Reference Number",
        input: "text",
        inputLabel: `${subTenderType} Reference Number`,
        inputPlaceholder: "Enter reference code",
        showCancelButton: true,
        confirmButtonText: "Submit",
        cancelButtonText: "Cancel",
        inputValidator: (value) => {
          if (!value || value.trim() === "") {
            return "Reference number is required.";
          }
          return null;
        },
      });

      if (!result.isConfirmed) return;
      subTenderDetails = result.value.trim();
    }

    try {
      const { user, token } = getAuthCredentials();
      const cashierId = userId;
      const cashierName = `${user?.firstname ?? ""} ${user?.lastname ?? ""}`.trim();
      console.log(cashierId, cashierName);
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/v1/api/order/process-order`,
        {
          posId,
          receiptNumber,
          tenderedAmount: amountToSend,
          cashierId,
          cashierName,
          ...(selectedSalesType === "E-Cash" && {
            salesType: "E-Cash",
            subTenderType,
            subTenderDetails,
          }),
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      Swal.fire({
        title: "Success",
        text: "Order processed successfully.",
        icon: "success",
        confirmButtonText: "OK",
      }).then(() => window.location.reload());
    } catch (err) {
      console.error(err);
      Swal.fire(
        "Error",
        err.response?.data?.message || "Failed to process order.",
        "error"
      );
    }
  };

  return (
    <Dialog open={open} onClose={handleClose}>
      <DialogTitle>Receipt Options</DialogTitle>
      <DialogContent>
        <Typography>
          {status === "pending"
            ? "Would you like to print the Order Slip or Override the receipt?"
            : "Would you like to print the Receipt or the Order Slip?"}
        </Typography>
      </DialogContent>
      <DialogActions>
        {status !== "pending" && (
          <>
            <Button onClick={() => handleAction("view")}>Print</Button>
            <Button onClick={() => handleAction("print")}>Download</Button>
          </>
        )}
        <Button onClick={() => handleAction("headerless")}>Order Slip</Button>
        {["E-Cash", "Cash"].includes(paymentType) && status === "pending" && (
          <Button color="error" onClick={handleOverride}>
            Override
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ReceiptActionModal;
